import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Mail, Send, CheckCircle, XCircle, AlertTriangle, Clock, 
  Building2, User, Calendar, DollarSign, FileText, Eye,
  ArrowLeft, Paperclip, Download, Star, Upload, Image, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ANNEXURE_TYPES = [
  { id: 'email_screenshot', label: 'Email Screenshot' },
  { id: 'hr_response', label: 'HR Response Document' },
  { id: 'verification_portal', label: 'Verification Portal Screenshot' },
  { id: 'phone_call_recording', label: 'Phone Call Recording' },
  { id: 'written_confirmation', label: 'Written Confirmation' },
  { id: 'document_scan', label: 'Document Scan' },
  { id: 'id_proof', label: 'ID Proof' },
  { id: 'other', label: 'Other' }
];

const VERIFICATION_FIELDS = [
  { id: 'candidate_name', label: 'Candidate Name', required: true },
  { id: 'employer_name', label: 'Employer/Company Name', required: true },
  { id: 'employee_code', label: 'Employee ID/Code', required: false },
  { id: 'designation', label: 'Designation', required: true },
  { id: 'date_of_joining', label: 'Date of Joining', required: true },
  { id: 'date_of_relieving', label: 'Date of Relieving', required: true },
  { id: 'last_drawn_salary', label: 'Last Drawn Salary (CTC)', required: false },
  { id: 'fixed_gross_salary', label: 'Fixed Gross Salary', required: false },
  { id: 'reason_for_leaving', label: 'Reason for Leaving', required: false },
  { id: 'reporting_manager', label: 'Reporting Manager', required: false },
  { id: 'payslip_available', label: 'Payslip/Salary Slip', required: false },
  { id: 'relieving_letter', label: 'Relieving Letter', required: false },
  { id: 'exit_formalities', label: 'Exit Formalities Completed', required: false },
  { id: 'attendance_punctuality', label: 'Attendance & Punctuality', required: false },
  { id: 'integrity_performance', label: 'Integrity & Performance', required: false },
  { id: 'disciplinary_issues', label: 'Any Disciplinary Issues', required: false },
  { id: 'eligible_for_rehire', label: 'Eligible for Rehire', required: false }
];

const NOT_VERIFIED_REASONS = [
  'Company Shut Down',
  'Company Not Responding',
  'HR Not Available',
  'Employee Records Not Found',
  'As per Company Policy',
  'Other'
];

const DISCREPANCY_REASONS = [
  'DOJ Not Matching',
  'DOR Not Matching',
  'Designation Not Matching',
  'Salary Not Matching',
  'Employee Code Not Matching',
  'Document Not Genuine',
  'Exit Formalities Pending',
  'Employee Never Worked Here',
  'Other'
];

const VERIFICATION_MODES = [
  'Mail-HR',
  'Mail-Supervisor',
  'Verbal-HR',
  'Verbal-Supervisor',
  'Proactive Mail',
  'Online',
  'Physical Visit',
  'Bar Code',
  'Third Party Verification',
  'UAN',
  'EPFO Office',
  'Digital Verification Platform',
  'MCA-Company Registry',
  'Other'
];

export default function EmploymentVerificationForm({ 
  caseId, 
  checkId, 
  checkData, 
  employeeData, 
  documents = [],
  onBack,
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showRecordResponse, setShowRecordResponse] = useState(false);
  const [verificationData, setVerificationData] = useState(checkData?.employment_verification || {});
  const [companyTemplate, setCompanyTemplate] = useState(null);
  
  // Annexure state
  const [annexures, setAnnexures] = useState([]);
  const [showUploadAnnexure, setShowUploadAnnexure] = useState(false);
  const [uploadingAnnexure, setUploadingAnnexure] = useState(false);
  const [annexureForm, setAnnexureForm] = useState({
    file_name: '',
    file_url: '',
    file_type: 'image',
    description: '',
    annexure_type: 'email_screenshot'
  });
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    employer_name: '',
    hr_email: '',
    hr_name: '',
    employment_index: 0,
    attached_documents: []
  });
  
  // Fetch annexures on load
  useEffect(() => {
    const fetchAnnexures = async () => {
      try {
        const res = await axios.get(`/cases/${caseId}/checks/${checkId}/annexures`);
        setAnnexures(res.data || []);
      } catch (e) {
        console.error('Failed to fetch annexures');
      }
    };
    if (caseId && checkId) {
      fetchAnnexures();
    }
  }, [caseId, checkId]);
  
  // Check for company template when employer changes
  useEffect(() => {
    const checkCompanyTemplate = async () => {
      const employerName = employeeData?.latest_employment?.employer_name;
      if (employerName) {
        try {
          const res = await axios.get(`/company-templates/${encodeURIComponent(employerName)}`);
          if (res.data && res.data.company_name) {
            setCompanyTemplate(res.data);
          }
        } catch (e) {
          setCompanyTemplate(null);
        }
      }
    };
    checkCompanyTemplate();
  }, [employeeData]);
  
  const handleUploadAnnexure = async () => {
    if (!annexureForm.file_url) {
      toast.error('Please provide file URL');
      return;
    }
    
    setUploadingAnnexure(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures`, {
        case_id: caseId,
        check_id: checkId,
        ...annexureForm
      });
      setAnnexures(prev => [...prev, res.data]);
      setShowUploadAnnexure(false);
      setAnnexureForm({
        file_name: '',
        file_url: '',
        file_type: 'image',
        description: '',
        annexure_type: 'email_screenshot'
      });
      toast.success('Annexure uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload annexure');
    } finally {
      setUploadingAnnexure(false);
    }
  };
  
  const handleDeleteAnnexure = async (annexureId) => {
    if (!confirm('Delete this annexure?')) return;
    
    try {
      await axios.delete(`/annexures/${annexureId}`);
      setAnnexures(prev => prev.filter(a => a.id !== annexureId));
      toast.success('Annexure deleted');
    } catch (error) {
      toast.error('Failed to delete annexure');
    }
  };
  
  // Response form state
  const [responseForm, setResponseForm] = useState({
    verifier_name: '',
    verifier_designation: '',
    verifier_email: '',
    verifier_phone: '',
    verification_mode: '',
    verification_mode_other: '',
    overall_remarks: '',
    fields: VERIFICATION_FIELDS.map(f => ({
      field_name: f.label,
      candidate_value: '',
      status: 'verified',
      reason: '',
      verified_value: '',
      hr_remarks: ''
    }))
  });

  useEffect(() => {
    // Pre-populate email form with employment data
    if (employeeData) {
      const latestEmp = employeeData.latest_employment || {};
      setEmailForm(prev => ({
        ...prev,
        employer_name: latestEmp.employer_name || '',
        hr_email: latestEmp.hr_email || '',
        hr_name: latestEmp.hr_name || ''
      }));
      
      // Pre-populate response form with candidate values
      const updatedFields = VERIFICATION_FIELDS.map(f => {
        let candidateValue = '';
        switch(f.id) {
          case 'candidate_name': candidateValue = employeeData.name || ''; break;
          case 'employer_name': candidateValue = latestEmp.employer_name || ''; break;
          case 'employee_code': candidateValue = latestEmp.employee_code || ''; break;
          case 'designation': candidateValue = latestEmp.designation || ''; break;
          case 'date_of_joining': candidateValue = latestEmp.date_of_joining || ''; break;
          case 'date_of_relieving': candidateValue = latestEmp.date_of_relieving || ''; break;
          case 'last_drawn_salary': candidateValue = latestEmp.salary || ''; break;
          case 'fixed_gross_salary': candidateValue = latestEmp.fixed_gross || ''; break;
          case 'reason_for_leaving': candidateValue = latestEmp.reason_for_leaving || ''; break;
          case 'reporting_manager': candidateValue = latestEmp.rm_name || ''; break;
          default: candidateValue = '';
        }
        return {
          field_name: f.label,
          candidate_value: candidateValue,
          status: 'verified',
          reason: '',
          verified_value: '',
          hr_remarks: ''
        };
      });
      setResponseForm(prev => ({ ...prev, fields: updatedFields }));
    }
    
    // Load existing verification data
    if (checkData?.employment_verification) {
      setVerificationData(checkData.employment_verification);
    }
  }, [employeeData, checkData]);

  const handleSendEmail = async () => {
    if (!emailForm.employer_name || !emailForm.hr_email) {
      toast.error('Please fill employer name and HR email');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/send-verification-email`, emailForm);
      toast.success('Verification email sent (MOCKED)');
      setVerificationData(prev => ({
        ...prev,
        initiated_at: res.data.initiated_at,
        email_sent: true,
        employer_name: emailForm.employer_name,
        hr_email: emailForm.hr_email
      }));
      setShowSendEmail(false);
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordResponse = async () => {
    if (!responseForm.verifier_name || !responseForm.verifier_designation) {
      toast.error('Please fill verifier name and designation');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/cases/${caseId}/checks/${checkId}/record-verification`, responseForm);
      toast.success(`Verification recorded - Result: ${res.data.overall_result.toUpperCase()}`);
      setVerificationData(prev => ({
        ...prev,
        ...res.data,
        fields: responseForm.fields,
        verifier_name: responseForm.verifier_name,
        verifier_designation: responseForm.verifier_designation
      }));
      setShowRecordResponse(false);
      if (onComplete) onComplete(res.data);
    } catch (error) {
      toast.error('Failed to record verification');
    } finally {
      setLoading(false);
    }
  };

  const updateFieldStatus = (index, field, value) => {
    setResponseForm(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], [field]: value };
      return { ...prev, fields: newFields };
    });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'verified': return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'not_verified': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'discrepancy': return <XCircle className="w-5 h-5 text-rose-600" />;
      default: return null;
    }
  };

  const getResultBadge = (result) => {
    switch(result) {
      case 'green': return <Badge className="bg-emerald-100 text-emerald-700">GREEN - Verified</Badge>;
      case 'amber': return <Badge className="bg-amber-100 text-amber-700">AMBER - Unable to Verify</Badge>;
      case 'red': return <Badge className="bg-rose-100 text-rose-700">RED - Discrepancy</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  const employmentDocs = documents.filter(d => 
    d.check_type === 'employment_check' || 
    ['relieving_letter', 'payslips', 'offer_letter', 'experience_letter'].includes(d.document_type)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Employment Verification</h2>
            <p className="text-slate-600">Case ID: {caseId} | Check ID: {checkId}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getResultBadge(verificationData?.overall_result)}
        </div>
      </div>

      {/* Special Company Warning */}
      {companyTemplate && companyTemplate.is_special_company && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <Star className="w-6 h-6 text-amber-600 fill-amber-500 mt-1" />
              <div className="flex-1">
                <p className="font-bold text-amber-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Special Company: {companyTemplate.company_name}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  This company has special verification requirements. Please follow the prescribed format.
                </p>
                {companyTemplate.mandatory_documents?.length > 0 && (
                  <div className="mt-2">
                    <span className="text-sm font-medium text-amber-800">Mandatory Documents: </span>
                    <span className="text-sm text-amber-700">{companyTemplate.mandatory_documents.join(', ')}</span>
                  </div>
                )}
                {companyTemplate.special_instructions && (
                  <div className="mt-2 p-2 bg-amber-100 rounded text-sm text-amber-800">
                    <strong>Instructions:</strong> {companyTemplate.special_instructions}
                  </div>
                )}
                {companyTemplate.verification_portal_url && (
                  <div className="mt-2">
                    <a 
                      href={companyTemplate.verification_portal_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Open Verification Portal →
                    </a>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TAT Info */}
      {verificationData?.initiated_at && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Clock className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Verification Timeline</p>
                  <p className="text-sm text-blue-700">
                    Initiated: {new Date(verificationData.initiated_at).toLocaleString()}
                    {verificationData.completed_at && (
                      <> | Completed: {new Date(verificationData.completed_at).toLocaleString()}</>
                    )}
                    {verificationData.tat_hours && (
                      <> | TAT: {verificationData.tat_hours} hours</>
                    )}
                  </p>
                </div>
              </div>
              {verificationData.email_sent && (
                <Badge className="bg-blue-100 text-blue-700">
                  <Mail className="w-3 h-3 mr-1" />
                  Email Sent
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Candidate Employment Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Candidate Employment Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employeeData?.latest_employment ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-500">Candidate Name</Label>
                <p className="font-medium">{employeeData.name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Employer</Label>
                <p className="font-medium">{employeeData.latest_employment.employer_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Employee Code</Label>
                <p className="font-medium">{employeeData.latest_employment.employee_code || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Designation</Label>
                <p className="font-medium">{employeeData.latest_employment.designation || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Date of Joining</Label>
                <p className="font-medium">{employeeData.latest_employment.date_of_joining || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Date of Relieving</Label>
                <p className="font-medium">{employeeData.latest_employment.date_of_relieving || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Last Drawn Salary</Label>
                <p className="font-medium">{employeeData.latest_employment.salary || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Reason for Leaving</Label>
                <p className="font-medium">{employeeData.latest_employment.reason_for_leaving || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Reporting Manager</Label>
                <p className="font-medium">{employeeData.latest_employment.rm_name || 'N/A'}</p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500">No employment information available</p>
          )}
        </CardContent>
      </Card>

      {/* Documents */}
      {employmentDocs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Candidate Documents ({employmentDocs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {employmentDocs.map((doc, idx) => (
                <Button 
                  key={idx} 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(doc.file_url, '_blank')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {doc.document_type}
                  <Download className="w-3 h-3 ml-2" />
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Annexures Section - Proof of Verification */}
      <Card className="border-indigo-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-indigo-600" />
            Annexures / Proof of Verification
          </CardTitle>
          <Button onClick={() => setShowUploadAnnexure(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="emp-upload-annexure-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload Annexure
          </Button>
        </CardHeader>
        <CardContent>
          {annexures.length === 0 ? (
            <p className="text-slate-500 text-center py-4">
              No annexures uploaded. Upload email screenshots or other proof of verification.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {annexures.map((annexure, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-slate-50 relative group" data-testid={`emp-annexure-item-${idx}`}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-600 h-6 w-6 p-0"
                    onClick={() => handleDeleteAnnexure(annexure.id)}
                    data-testid={`emp-delete-annexure-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-2 mb-2">
                    {annexure.file_type === 'image' ? (
                      <Image className="w-5 h-5 text-indigo-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-indigo-600" />
                    )}
                    <Badge variant="outline" className="text-xs">
                      {ANNEXURE_TYPES.find(t => t.id === annexure.annexure_type)?.label || annexure.annexure_type}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium truncate">{annexure.file_name || 'Annexure'}</p>
                  {annexure.description && (
                    <p className="text-xs text-slate-500 truncate">{annexure.description}</p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full text-xs h-7"
                      onClick={() => window.open(annexure.file_url, '_blank')}
                      data-testid={`emp-view-annexure-${idx}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {annexure.uploaded_at ? new Date(annexure.uploaded_at).toLocaleDateString() : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4">
        {!verificationData?.email_sent && (
          <Button onClick={() => setShowSendEmail(true)} className="bg-blue-600 hover:bg-blue-700">
            <Mail className="w-4 h-4 mr-2" />
            Send Verification Email
          </Button>
        )}
        <Button onClick={() => setShowRecordResponse(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Record Verification Response
        </Button>
      </div>

      {/* Verification Results (if completed) */}
      {verificationData?.fields && verificationData.fields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Verification Results
              </span>
              {getResultBadge(verificationData.overall_result)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field</TableHead>
                  <TableHead>Candidate Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason/Remarks</TableHead>
                  <TableHead>Verified Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {verificationData.fields.map((field, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{field.field_name}</TableCell>
                    <TableCell>{field.candidate_value || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(field.status)}
                        <span className="capitalize">{field.status?.replace('_', ' ')}</span>
                      </div>
                    </TableCell>
                    <TableCell>{field.reason || field.hr_remarks || '-'}</TableCell>
                    <TableCell>{field.verified_value || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Verifier Info */}
            {verificationData.verifier_name && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2">Verified By</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <Label className="text-slate-500">Name</Label>
                    <p>{verificationData.verifier_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Designation</Label>
                    <p>{verificationData.verifier_designation}</p>
                  </div>
                  {verificationData.verifier_email && (
                    <div>
                      <Label className="text-slate-500">Email</Label>
                      <p>{verificationData.verifier_email}</p>
                    </div>
                  )}
                  {verificationData.verifier_phone && (
                    <div>
                      <Label className="text-slate-500">Phone</Label>
                      <p>{verificationData.verifier_phone}</p>
                    </div>
                  )}
                </div>
                {verificationData.verification_mode && (
                  <div className="mt-2">
                    <Label className="text-slate-500">Verification Mode</Label>
                    <p className="font-medium">
                      {verificationData.verification_mode === 'Other' 
                        ? verificationData.verification_mode_other || 'Other'
                        : verificationData.verification_mode}
                    </p>
                  </div>
                )}
                {verificationData.overall_remarks && (
                  <div className="mt-2">
                    <Label className="text-slate-500">Overall Remarks</Label>
                    <p>{verificationData.overall_remarks}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Send Email Dialog */}
      <Dialog open={showSendEmail} onOpenChange={setShowSendEmail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Verification Email to HR
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Employer/Company Name *</Label>
              <Input 
                value={emailForm.employer_name}
                onChange={(e) => setEmailForm(prev => ({ ...prev, employer_name: e.target.value }))}
                placeholder="Enter company name"
              />
            </div>
            <div>
              <Label>HR Email *</Label>
              <Input 
                type="email"
                value={emailForm.hr_email}
                onChange={(e) => setEmailForm(prev => ({ ...prev, hr_email: e.target.value }))}
                placeholder="hr@company.com"
              />
            </div>
            <div>
              <Label>HR Name</Label>
              <Input 
                value={emailForm.hr_name}
                onChange={(e) => setEmailForm(prev => ({ ...prev, hr_name: e.target.value }))}
                placeholder="HR Contact Name"
              />
            </div>
            <div>
              <Label>Employment Period</Label>
              <Select 
                value={String(emailForm.employment_index)} 
                onValueChange={(v) => setEmailForm(prev => ({ ...prev, employment_index: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Latest Employment</SelectItem>
                  {employeeData?.past_employments?.map((emp, idx) => (
                    <SelectItem key={idx} value={String(idx + 1)}>
                      Past Employment {idx + 1}: {emp.employer_name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {employmentDocs.length > 0 && (
              <div>
                <Label>Attach Documents</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {employmentDocs.map((doc, idx) => (
                    <Button
                      key={idx}
                      variant={emailForm.attached_documents.includes(doc.id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setEmailForm(prev => ({
                          ...prev,
                          attached_documents: prev.attached_documents.includes(doc.id)
                            ? prev.attached_documents.filter(id => id !== doc.id)
                            : [...prev.attached_documents, doc.id]
                        }));
                      }}
                    >
                      <Paperclip className="w-3 h-3 mr-1" />
                      {doc.document_type}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendEmail(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponse} onOpenChange={setShowRecordResponse}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Record Verification Response
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Verifier Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Verifier Name *</Label>
                <Input 
                  value={responseForm.verifier_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_name: e.target.value }))}
                  placeholder="HR Name"
                />
              </div>
              <div>
                <Label>Verifier Designation *</Label>
                <Input 
                  value={responseForm.verifier_designation}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_designation: e.target.value }))}
                  placeholder="HR Manager"
                />
              </div>
              <div>
                <Label>Verifier Email</Label>
                <Input 
                  type="email"
                  value={responseForm.verifier_email}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_email: e.target.value }))}
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <Label>Verifier Phone</Label>
                <Input 
                  value={responseForm.verifier_phone}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_phone: e.target.value }))}
                  placeholder="+91-XXXXXXXXXX"
                />
              </div>
            </div>

            {/* Verification Mode */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div>
                <Label>Verification Mode *</Label>
                <Select 
                  value={responseForm.verification_mode}
                  onValueChange={(v) => setResponseForm(prev => ({ ...prev, verification_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select verification mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {VERIFICATION_MODES.map((mode, idx) => (
                      <SelectItem key={idx} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {responseForm.verification_mode === 'Other' && (
                <div>
                  <Label>Specify Other Mode *</Label>
                  <Input 
                    value={responseForm.verification_mode_other}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, verification_mode_other: e.target.value }))}
                    placeholder="Enter verification mode"
                  />
                </div>
              )}
            </div>

            {/* Field Verification */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="w-[200px]">Field</TableHead>
                    <TableHead className="w-[150px]">Candidate Value</TableHead>
                    <TableHead className="w-[200px]">Verification Status</TableHead>
                    <TableHead>Reason (if not verified/discrepancy)</TableHead>
                    <TableHead className="w-[150px]">Verified Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {responseForm.fields.map((field, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {field.field_name}
                        {VERIFICATION_FIELDS[idx]?.required && <span className="text-rose-500 ml-1">*</span>}
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={field.candidate_value}
                          onChange={(e) => updateFieldStatus(idx, 'candidate_value', e.target.value)}
                          className="h-8 text-sm"
                          placeholder="-"
                        />
                      </TableCell>
                      <TableCell>
                        <RadioGroup 
                          value={field.status}
                          onValueChange={(v) => updateFieldStatus(idx, 'status', v)}
                          className="flex flex-col gap-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="verified" id={`verified-${idx}`} />
                            <Label htmlFor={`verified-${idx}`} className="text-xs text-emerald-600 cursor-pointer">
                              ✓ Verified
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="not_verified" id={`not-verified-${idx}`} />
                            <Label htmlFor={`not-verified-${idx}`} className="text-xs text-amber-600 cursor-pointer">
                              ! Not Verified
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="discrepancy" id={`discrepancy-${idx}`} />
                            <Label htmlFor={`discrepancy-${idx}`} className="text-xs text-rose-600 cursor-pointer">
                              ✗ Discrepancy
                            </Label>
                          </div>
                        </RadioGroup>
                      </TableCell>
                      <TableCell>
                        {field.status === 'not_verified' && (
                          <Select 
                            value={field.reason}
                            onValueChange={(v) => updateFieldStatus(idx, 'reason', v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select reason" />
                            </SelectTrigger>
                            <SelectContent>
                              {NOT_VERIFIED_REASONS.map((r, i) => (
                                <SelectItem key={i} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.status === 'discrepancy' && (
                          <Select 
                            value={field.reason}
                            onValueChange={(v) => updateFieldStatus(idx, 'reason', v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select reason" />
                            </SelectTrigger>
                            <SelectContent>
                              {DISCREPANCY_REASONS.map((r, i) => (
                                <SelectItem key={i} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {(field.status === 'not_verified' || field.status === 'discrepancy') && field.reason === 'Other' && (
                          <Input 
                            className="mt-2 h-8 text-xs"
                            placeholder="Specify reason"
                            value={field.hr_remarks}
                            onChange={(e) => updateFieldStatus(idx, 'hr_remarks', e.target.value)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {field.status === 'discrepancy' && (
                          <Input 
                            value={field.verified_value}
                            onChange={(e) => updateFieldStatus(idx, 'verified_value', e.target.value)}
                            className="h-8 text-sm"
                            placeholder="Correct value"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Overall Remarks */}
            <div>
              <Label>Overall Remarks</Label>
              <Textarea 
                value={responseForm.overall_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, overall_remarks: e.target.value }))}
                placeholder="Any additional comments or observations..."
                rows={3}
              />
            </div>

            {/* Legend */}
            <div className="flex gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span>All Verified = GREEN</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>Any Not Verified = AMBER</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-rose-600" />
                <span>Any Discrepancy = RED</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponse(false)}>Cancel</Button>
            <Button onClick={handleRecordResponse} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Annexure Dialog */}
      <Dialog open={showUploadAnnexure} onOpenChange={setShowUploadAnnexure}>
        <DialogContent className="max-w-lg" data-testid="emp-upload-annexure-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              Upload Annexure / Proof
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Annexure Type *</Label>
              <Select 
                value={annexureForm.annexure_type}
                onValueChange={(v) => setAnnexureForm(prev => ({ ...prev, annexure_type: v }))}
              >
                <SelectTrigger data-testid="emp-annexure-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ANNEXURE_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File Name</Label>
              <Input 
                value={annexureForm.file_name}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_name: e.target.value }))}
                placeholder="e.g., HR_Email_Response"
                data-testid="emp-annexure-filename-input"
              />
            </div>
            <div>
              <Label>File URL *</Label>
              <Input 
                value={annexureForm.file_url}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_url: e.target.value }))}
                placeholder="https://example.com/image.png"
                data-testid="emp-annexure-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Upload your file to a cloud storage (Google Drive, Dropbox, etc.) and paste the public URL here.
              </p>
            </div>
            <div>
              <Label>File Type</Label>
              <Select 
                value={annexureForm.file_type}
                onValueChange={(v) => setAnnexureForm(prev => ({ ...prev, file_type: v }))}
              >
                <SelectTrigger data-testid="emp-annexure-filetype-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={annexureForm.description}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the annexure..."
                rows={2}
                data-testid="emp-annexure-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAnnexure(false)}>Cancel</Button>
            <Button 
              onClick={handleUploadAnnexure} 
              disabled={uploadingAnnexure || !annexureForm.file_url} 
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="emp-annexure-submit-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingAnnexure ? 'Uploading...' : 'Upload Annexure'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
